import { Component, Input } from '@builder.io/sdk';

import sectionFields from 'molecules/Section/config';

const tableCell = [
  {
    name: 'cell',
    type: 'string',
    enum: [
      { label: 'Check', value: 'check' },
      { label: 'Indiscriminate', value: 'indiscriminate' },
      { label: 'Text', value: 'text' },
    ],
    required: true,
    defaultValue: 'indiscriminate',
    helperText: 'Select the cell type: check mark, indiscriminate (empty), or custom text.',
  },
  {
    name: 'text',
    type: 'string',
    showIf: "options.get('cell') === 'text'",
    helperText: 'Custom text to display when "Text" is selected as the cell type.',
  },
] as const satisfies DeepReadonly<Input[]>;

const tableRow = [
  {
    name: 'rowHeading',
    type: 'string',
    helperText: 'Label for the row (e.g., a feature or plan detail).',
  },
  {
    name: 'description',
    type: 'string',
    helperText: 'Optional description shown as a tooltip for this row.',
  },
  {
    name: 'pro',
    type: 'object',
    subFields: tableCell,
    helperText: 'Cell content for the Pro plan.',
  },
  {
    name: 'scale',
    type: 'object',
    subFields: tableCell,
    helperText: 'Cell content for the Scale plan.',
  },
  {
    name: 'enterprise',
    type: 'object',
    subFields: tableCell,
    helperText: 'Cell content for the Enterprise plan.',
  },
] as const satisfies DeepReadonly<Input[]>;

const pricingTableFields = [
  {
    name: 'tableHeading',
    type: 'string',
    helperText: 'Main heading for the pricing table.',
  },
  {
    name: 'proLink',
    type: 'string',
    helperText: 'URL for the Pro plan CTA button.',
  },
  {
    name: 'proDescription',
    type: 'string',
    helperText: 'Short description of the Pro plan.',
  },
  {
    name: 'scaleLink',
    type: 'string',
    helperText: 'URL for the Scale plan CTA button.',
  },
  {
    name: 'scaleDescription',
    type: 'string',
    helperText: 'Short description of the Scale plan.',
  },
  {
    name: 'enterpriseLink',
    type: 'string',
    helperText: 'URL for the Enterprise plan CTA button.',
  },
  {
    name: 'enterpriseDescription',
    type: 'string',
    helperText: 'Short description of the Enterprise plan.',
  },
  {
    name: 'tableSections',
    type: 'list',
    subFields: [
      { name: 'id', type: 'string', required: true, helperText: 'Auto-generated unique ID for each section.' },
      { name: 'label', type: 'string', defaultValue: 'Plan Details', helperText: 'Section label (e.g., Features).' },
      {
        name: 'rows',
        type: 'list',
        subFields: tableRow,
        helperText: 'Add rows to compare features across Pro, Scale, and Enterprise.',
      },
    ],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onChange: (options: Map<string, any>) => {
      if (options.get('tableSections').length > 0) {
        options.get('tableSections').forEach((item: Map<string, string>, index: number) => {
          item.set('id', `tableSections-${index}`);
        });
      }
    },
    helperText: 'List of table sections. Each section contains multiple feature rows.',
  },
  ...sectionFields,
] as const satisfies DeepReadonly<Input[]>;

/**
 * Pricing Table Config
 * @deprecated This configuration is maintained for legacy support, but all new implementations should use V2.
 * @note Needs to be removed after migration of content to new pricing table `v2: Pricing Table`
 */
const pricingTableConfig = {
  name: 'Pricing Table',
  hideFromInsertMenu: true,
  inputs: pricingTableFields,
} as Component;

export type PricingCellProps = GenerateBuilder<typeof tableCell>;
export type PricingRowProps = GenerateBuilder<typeof tableRow>;
export type PricingTableConfigProps = GenerateBuilder<typeof pricingTableFields>;

export default pricingTableConfig;
